// this is "share-collab"
import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { $trackDocumentShared } from "../../analytics";
import { $purgeDocument } from "../../document-loader/loader.atom";
import { $gitStatus } from "../../git";
import { type Workspace } from "../../workspaces/workspace.types";
import { $setRemoteWorkspace, $updateDocumentPermissions } from "../sharing";
import type { PermissionUpdate } from "../sharing/permissions.types";
import { type PlannedOutcomeResult } from "./current-state.workflow.atom";
import {
	NoDocumentIdError,
	NoWorkspaceForRemoteRepoCreationError,
	PlanNotAchievableError,
	UnknownError,
	WorkflowError,
} from "./errors";
import {
	$captureCollabInitSnapshot,
	$commitChanges,
	$createCollabBranch,
	$createPublishRepo,
	$createRemote,
	$initializeCollab,
	$initializeGitRepo,
	$pushCollabBranch,
	$pushToRemote,
	$setCollabTrackingBranch,
	$switchToCollabBranch,
	$verifyRemoteExists,
	GIT_COLLAB_BRANCH,
	initialCommit,
} from "./mutations.atom";
import {
	$appendToSharePlan,
	$isSyncing,
	$setSharePlan,
	$syncAction,
	$syncActionWithLogging,
	$syncError,
	$syncState,
} from "./sync-state.atom";
import type { SyncAction, SyncSnapshot } from "./sync.types";

const logger = new Logger("share-sync.workflow.atom");

const setAction = (
	set: any,
	documentId: string,
	action: SyncAction | undefined,
	dryRun?: boolean
) => {
	if (dryRun && action) {
		set($appendToSharePlan, { documentId, action });
		return;
	}
	set($syncActionWithLogging, {
		documentId,
		action,
		workflow: "share-collab",
	});
};

// This shares a document to collab but does not publish.
export const $executeShareCollabWorkflow = atom(
	null,
	async (
		get,
		set,
		input: {
			snap: SyncSnapshot;
			plannedOutcome: PlannedOutcomeResult;
			pendingPermissions: Map<string, PermissionUpdate>;
			workspace?: Workspace;
			dryRun?: boolean;
			skipCollab?: boolean;
		}
	) => {
		const { dryRun, skipCollab } = input;
		const documentId = input.snap.documentId!;
		logger.debug("Starting share sync workflow", { input, dryRun });

		// Reset plan at start if dry-run
		if (dryRun) {
			set($setSharePlan, { documentId, plan: [] });
		} else {
			set($isSyncing(documentId), true);
		}
		try {
			// STEP 0: GET INITIAL STATE DATA
			const { snap, plannedOutcome, pendingPermissions } = input;
			let publishWorkspace = snap.publishWorkspace;

			//
			// VALIDATE DATA
			//
			// Validate if we can even execute
			if (!documentId) {
				throw new NoDocumentIdError("share-collab");
			}

			if (!plannedOutcome.canExecute) {
				logger.error("Cannot execute share sync workflow, plan not achievable", {
					planStatus: plannedOutcome.planStatus,
					plannedOutcome: plannedOutcome.plannedOutcome,
				});
				throw new PlanNotAchievableError("share-collab");
			}

			if (!snap.remoteRepoExists && !input.workspace) {
				logger.error(
					"Cannot execute share sync workflow, no workspace provided for remote repo creation"
				);
				throw new NoWorkspaceForRemoteRepoCreationError("share-collab");
			}

			// Capture collab initialization snapshot up-front, before any workflow mutations.
			// This keeps retries deterministic and avoids taking a snapshot from partially mutated state.
			const collabSnapshot =
				!dryRun && !snap.collabInitialized && !skipCollab
					? await set($captureCollabInitSnapshot, { documentId })
					: null;

			if (!dryRun && !snap.collabInitialized && !skipCollab && !collabSnapshot) {
				throw new WorkflowError(
					"Failed to capture document snapshot — metadata is not loaded"
				);
			}

			//
			// EXECUTE SYNC ACTIONS
			//

			// STEP 1: Local git stuff. Ensure git is initialized

			let sourceCommit: string | undefined;

			if (!snap.gitRepoStatus) {
				logger.debug("Initializing git repository");
				setAction(set, documentId, "initialize-git-repo", dryRun);
				if (!dryRun) {
					await set($initializeGitRepo, { documentId });
				}
				setAction(set, documentId, "commit-initial-changes", dryRun);
				if (!dryRun) {
					sourceCommit = await initialCommit(set, { documentId });
				}
				logger.debug("Git repository initialized");
			}

			if (!snap.remoteRepoExists) {
				// Commit any outstanding changes before pushing so that
				// moment.yml and other files are included in the push.
				if (!snap.repoClean && !sourceCommit) {
					setAction(set, documentId, "commit-outstanding-changes", dryRun);
					if (!dryRun) {
						try {
							sourceCommit = await set($commitChanges, { documentId });
						} catch (commitError) {
							// In jj, the working copy can report as dirty even when there
							// are no actual file changes to commit. This is safe to ignore
							// — files are already committed in the parent.
							const msg =
								commitError instanceof Error
									? commitError.message
									: String(commitError);
							if (msg.includes("described commit is empty")) {
								logger.debug(
									"No outstanding changes to commit (empty working copy), proceeding",
									{ documentId }
								);
							} else {
								throw commitError;
							}
						}
					}
				}

				logger.debug("Creating remote repository");
				setAction(set, documentId, "create-publish-repo", dryRun);
				if (!dryRun) {
					await set($createPublishRepo, { documentId, workspace: input.workspace! });
				}
				if (!snap.documentRemoteSet) {
					setAction(set, documentId, "create-remote", dryRun);
					if (!dryRun) {
						snap.momentRemoteUrl = await set($createRemote, {
							documentId,
							workspace: input.workspace!,
						});
						logger.debug(`moment remote url set to: ${snap.momentRemoteUrl}`);
					}
				}

				publishWorkspace = input.workspace!;
				logger.debug(
					"Remote repository created and initial commit pushed. Publish workspace set",
					{ publishWorkspace }
				);

				setAction(set, documentId, "push-to-remote", dryRun);
				await set($pushToRemote, { documentId, sourceCommit, dryRun });
			}

			// STEP 2: UPDATE PERMISSIONS
			setAction(set, documentId, "verify-remote-exists", dryRun);
			if (!dryRun) {
				await set($verifyRemoteExists, {
					documentId,
					workspace: publishWorkspace!,
				});
			}
			if (pendingPermissions && pendingPermissions.size > 0) {
				setAction(set, documentId, "update-permissions", dryRun);
				if (!dryRun) {
					await set($updateDocumentPermissions, {
						documentId,
						workspaceName: publishWorkspace!.name,
						updates: pendingPermissions,
					});
				}
			}

			// not implemented
			//
			// if (hasBaseTables) {
			// 	actions.add("ensure-reboot-tables-created");
			// }

			// STEP 3: DO COLLAB STUFF
			// If collab is already initialized, the branch exists on remote - nothing to do.
			// If skipCollab is set, skip collab initialization entirely (share without live editing).
			if (!snap.collabInitialized && !skipCollab) {
				// Create collab branch if it doesn't exist locally
				if (!snap.branches?.find((b) => b.name === GIT_COLLAB_BRANCH)) {
					// Commit any outstanding changes before creating the collab
					// branch so they aren't lost when switching branches.
					if (!snap.repoClean && !sourceCommit) {
						setAction(set, documentId, "commit-outstanding-changes", dryRun);
						if (!dryRun) {
							try {
								sourceCommit = await set($commitChanges, { documentId });
							} catch (commitError) {
								const msg =
									commitError instanceof Error
										? commitError.message
										: String(commitError);
								if (msg.includes("described commit is empty")) {
									logger.debug(
										"No outstanding changes to commit before collab branch, proceeding",
										{ documentId }
									);
								} else {
									throw commitError;
								}
							}
						}
					}

					setAction(set, documentId, "create-collab-branch", dryRun);
					if (!dryRun) {
						await set($createCollabBranch, documentId);
						await set($switchToCollabBranch, documentId);
					}
				}

				setAction(set, documentId, "push-collab-branch", dryRun);
				if (!dryRun) {
					if (!sourceCommit) {
						const status = await get($gitStatus(documentId));
						if (status?.isDraft) {
							const { $gitHistory } = await import("../../git");
							const history = await get($gitHistory(documentId));
							const wc = history?.commits.find((c) => c.hash === status.commit);
							sourceCommit = wc?.parents[0];
						}
					}
					await set($pushCollabBranch, { documentId, sourceCommit });
				}

				setAction(set, documentId, "set-collab-tracking-branch", dryRun);
				if (!dryRun) {
					await set($setCollabTrackingBranch, documentId);
				}

				setAction(set, documentId, "initialize-collab", dryRun);
				if (!dryRun) {
					logger.debug(`workspace is ${publishWorkspace?.id}`, {
						publishWorkspace,
					});
					await set($initializeCollab, {
						documentId,
						workspace: publishWorkspace!,
						snapshot: collabSnapshot!,
					});

					// Purge current state so document reloads from Reboot
					set($purgeDocument, documentId);
				}
			}

			if (!dryRun) {
				// refresh git status.
				set($gitStatus(documentId));

				// CLEANUP!
				set($setRemoteWorkspace, { documentId, workspace: input.workspace! });
				set($syncError(documentId), null);

				// Track share analytics
				set($trackDocumentShared, {
					documentId,
					workspaceSlug: (input.workspace ?? snap.publishWorkspace)!.slug,
					isFirstShare: !snap.remoteRepoExists,
				});

				logger.debug("Share collab workflow completed");
			}
		} catch (error) {
			if (!dryRun) {
				logger.error("Error executing share collab workflow", {
					error,
					syncAction: get($syncAction(documentId)),
					input,
				});

				if (!(error instanceof WorkflowError)) {
					set($syncError(documentId), new UnknownError("share-collab", error));
				} else {
					set($syncError(documentId), error);
				}
			}
		} finally {
			// RESET SYNC STATE (skip in dry-run mode)
			if (!dryRun) {
				set($syncState(documentId), { isSyncing: false, workflow: undefined });
				setAction(set, documentId, undefined);
				set($gitStatus(documentId));
			}
		}
	}
);
